import { getAppCopy } from '../i18n';

export type ImportLaunchTarget =
  | { kind: 'server'; serverUrl: string; sheetName: string }
  | { kind: 'file'; fileUrl: string };

const POUCHES_ORIGIN = 'https://apps.tomippe.jp';

export function fileNameFromParam(value: string): string {
  return value.endsWith('.pouches') ? value : `${value}.pouches`;
}

export function buildServerImportUrl(importParam: string, isPublic = false): string {
  const fileName = fileNameFromParam(importParam);
  const folder = isPublic ? 'import_public' : 'import';
  return `${POUCHES_ORIGIN}/pouches/${folder}/${fileName}`;
}

function sheetNameFromFileName(fileName: string): string {
  return fileName.replace(/\.pouches$/i, '');
}

/** 共有 URL・Universal Link・カスタムスキームからインポート先を解決 */
export function parseImportLaunchUrl(rawUrl: string): ImportLaunchTarget | null {
  if (!rawUrl) return null;

  try {
    if (rawUrl.startsWith('jp.tomippe.pouches://')) {
      const query = rawUrl.includes('?') ? rawUrl.slice(rawUrl.indexOf('?') + 1) : '';
      const nested = new URLSearchParams(query).get('url');
      if (nested) return parseImportLaunchUrl(nested);
    }

    const url = new URL(rawUrl, POUCHES_ORIGIN);

    const importParam = url.searchParams.get('import');
    if (importParam) {
      const fileName = fileNameFromParam(importParam);
      return {
        kind: 'server',
        serverUrl: `${POUCHES_ORIGIN}/pouches/import/${fileName}`,
        sheetName: sheetNameFromFileName(fileName),
      };
    }

    const importPublic = url.searchParams.get('import_public');
    if (importPublic) {
      const fileName = fileNameFromParam(importPublic);
      return {
        kind: 'server',
        serverUrl: `${POUCHES_ORIGIN}/pouches/import_public/${fileName}`,
        sheetName: sheetNameFromFileName(fileName),
      };
    }

    if (url.pathname.includes('/import_public/') && url.pathname.endsWith('.pouches')) {
      const fileName = url.pathname.split('/').pop() || 'imported.pouches';
      return {
        kind: 'server',
        serverUrl: url.href,
        sheetName: sheetNameFromFileName(fileName),
      };
    }

    if (url.pathname.includes('/import/') && url.pathname.endsWith('.pouches')) {
      const fileName = url.pathname.split('/').pop() || 'imported.pouches';
      return {
        kind: 'server',
        serverUrl: url.href,
        sheetName: sheetNameFromFileName(fileName),
      };
    }

    if (rawUrl.includes('.pouches')) {
      return { kind: 'file', fileUrl: rawUrl };
    }
  } catch {
    return null;
  }

  return null;
}

export function applyImportPageTitle(sheetName: string): void {
  document.title = `${sheetName} - POUCHES`;
  const metaDescription = document.querySelector('meta[name="description"]');
  if (metaDescription) {
    metaDescription.setAttribute('content', getAppCopy().meta.importSheet(sheetName));
  }
}

/** Edge: Store 起動を試す間、Web インポートを止める（open-in-app.js と共有） */
export const STORE_HANDOFF_PENDING_KEY = 'pouches_store_handoff_pending';
export const STORE_ALLOW_WEB_IMPORT_KEY = 'pouches_allow_web_import';

export function setStoreHandoffPending(): void {
  try {
    sessionStorage.setItem(STORE_HANDOFF_PENDING_KEY, '1');
  } catch {
    // ignore
  }
}

export function allowWebImportAfterStoreHandoff(href: string): void {
  try {
    sessionStorage.removeItem(STORE_HANDOFF_PENDING_KEY);
    sessionStorage.setItem(STORE_ALLOW_WEB_IMPORT_KEY, href);
  } catch {
    // ignore
  }
}

export function isWebImportAllowedAfterStoreHandoff(href: string): boolean {
  try {
    return sessionStorage.getItem(STORE_ALLOW_WEB_IMPORT_KEY) === href;
  } catch {
    return false;
  }
}

export function isStoreHandoffBlockingWebImport(href: string): boolean {
  try {
    if (sessionStorage.getItem(STORE_HANDOFF_PENDING_KEY) !== '1') return false;
    return sessionStorage.getItem(STORE_ALLOW_WEB_IMPORT_KEY) !== href;
  } catch {
    return false;
  }
}

export function clearStoreHandoffImportFlags(): void {
  try {
    sessionStorage.removeItem(STORE_HANDOFF_PENDING_KEY);
    sessionStorage.removeItem(STORE_ALLOW_WEB_IMPORT_KEY);
  } catch {
    // ignore
  }
}

const IMPORT_DECLINE_PREFIX = 'pouches_import_declined:';
const IMPORT_PENDING_PREFIX = 'pouches_import_pending:';
const PENDING_IMPORT_URL_KEY = 'pouches_pending_import_url';

export function rememberPendingImportLaunchUrl(rawUrl: string): void {
  try {
    sessionStorage.setItem(PENDING_IMPORT_URL_KEY, rawUrl);
  } catch {
    // ignore
  }
}

export function getPendingImportLaunchUrl(): string | null {
  try {
    return sessionStorage.getItem(PENDING_IMPORT_URL_KEY);
  } catch {
    return null;
  }
}

export function clearPendingImportLaunchUrl(): void {
  try {
    sessionStorage.removeItem(PENDING_IMPORT_URL_KEY);
  } catch {
    // ignore
  }
}

export function importLaunchDeclineKey(serverFilePath: string): string {
  return `${IMPORT_DECLINE_PREFIX}${serverFilePath}`;
}

export function markImportLaunchDeclined(serverFilePath: string): void {
  try {
    sessionStorage.setItem(importLaunchDeclineKey(serverFilePath), '1');
  } catch {
    // ignore
  }
}

export function isImportLaunchDeclined(serverFilePath: string): boolean {
  try {
    return sessionStorage.getItem(importLaunchDeclineKey(serverFilePath)) === '1';
  } catch {
    return false;
  }
}

export function clearImportLaunchDeclined(serverFilePath: string): void {
  try {
    sessionStorage.removeItem(importLaunchDeclineKey(serverFilePath));
  } catch {
    // ignore
  }
}

export function markImportPromptPending(serverFilePath: string): void {
  try {
    sessionStorage.setItem(`${IMPORT_PENDING_PREFIX}${serverFilePath}`, '1');
  } catch {
    // ignore
  }
}

export function clearImportPromptPending(serverFilePath: string): void {
  try {
    sessionStorage.removeItem(`${IMPORT_PENDING_PREFIX}${serverFilePath}`);
  } catch {
    // ignore
  }
}

export function isImportPromptPending(serverFilePath: string): boolean {
  try {
    return sessionStorage.getItem(`${IMPORT_PENDING_PREFIX}${serverFilePath}`) === '1';
  } catch {
    return false;
  }
}

/** インポート成功時のみ URL から import パラメータを除去 */
export function clearImportLaunchUrl(): void {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('import') && !url.searchParams.has('import_public')) return;
    url.searchParams.delete('import');
    url.searchParams.delete('import_public');
    url.searchParams.delete('_pouches_launch');
    const search = url.searchParams.toString();
    const next = `${url.pathname}${search ? `?${search}` : ''}${url.hash}`;
    window.history.replaceState(null, '', next || '/pouches/run/');
  } catch {
    // Capacitor 等で location が特殊な場合は無視
  }
}
